import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { History, Save, FilePlus2, Share2, Link2Off, Copy, FolderInput, Trash2, ArchiveRestore, Import, Inbox, ChevronRight, Eraser, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { listActivity, clearActivity, apiErrorMessage } from '../../lib/projectApi';
import { relativeTime } from '../../lib/recents';

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const q = (s) => `“${s}”`;

const ICONS = { created: FilePlus2, saved: Save, received: Inbox, shared: Share2, unshared: Link2Off, copied: Copy, moved: FolderInput, deleted: Trash2, restored: ArchiveRestore, imported: Import };

/** One sentence per feed line — plain words, no ids. Exported for tests. */
export const describeActivity = (it) => {
  const name = it.project_name ? q(it.project_name) : 'a publication';
  const folder = it.folder_name ? q(it.folder_name) : null;
  const many = (it.count || 0) > 1 || (!it.project_name && it.count);
  switch (it.kind) {
    case 'created': return `Created ${name}`;
    case 'saved': return `Saved ${name}`;
    case 'received': return `Received ${name}${it.detail ? ` from ${it.detail}` : ''}`;
    case 'shared': return `Shared ${name} with a link`;
    case 'unshared': return `Stopped sharing ${name}`;
    case 'copied': return it.detail ? `Copied ${q(it.detail)} as ${name}` : `Made a copy: ${name}`;
    case 'moved': return `Moved ${many ? plural(it.count, 'publication') : name} to ${folder || 'Unfiled'}`;
    case 'deleted':
      if (folder) return `Moved folder ${folder} to the Trash${it.count ? ` with ${plural(it.count, 'publication')}` : ''}`;
      return `Moved ${many ? plural(it.count, 'publication') : name} to the Trash`;
    case 'restored':
      if (folder) return `Restored folder ${folder}${it.count ? ` with ${plural(it.count, 'publication')}` : ''}`;
      return `Restored ${many ? plural(it.count, 'publication') : name}`;
    case 'imported': return `Imported ${it.detail || plural(it.count || 0, 'publication')}`;
    default: return it.kind;
  }
};

/**
 * Home ▸ Recent activity — the last few things that changed in the library (saved, shared, imported, copied, moved,
 * deleted, restored), newest first, so people can pick up where they left off. A line opens its publication, or the
 * Trash when the publication is there. "Clear" empties the list (the publications themselves are untouched).
 */
export const RecentActivity = ({ go, onOpen, limit = 8 }) => {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [clearing, setClearing] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try { const res = await listActivity(limit); setItems(res.items || []); } catch (e) { setError(await apiErrorMessage(e, 'Could not load your recent activity.')); setItems([]); }
  }, [limit]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const h = () => load(); window.addEventListener('wp:library-changed', h); return () => window.removeEventListener('wp:library-changed', h); }, [load]);

  const clear = async () => {
    setClearing(true);
    try { await clearActivity(); setItems([]); toast.success('Recent activity cleared', { description: 'Your publications are untouched.' }); } catch (e) { toast.error(await apiErrorMessage(e, 'Could not clear the list.')); } finally { setClearing(false); }
  };
  const activate = (it) => {
    if (it.project_id && it.project_state === 'live' && onOpen) return onOpen(it.project_id);
    if (it.project_state === 'trashed' || (it.kind === 'deleted' && go)) return go?.('trash');
    if (it.folder_id && (it.kind === 'moved' || it.kind === 'restored') && go) return go('open', { folderId: it.folder_id });
    if (it.kind === 'imported' && go) return go('open');
    return undefined;
  };
  const hint = (it) => {
    if (it.project_id && it.project_state === 'live') return 'Open';
    if (it.project_state === 'trashed' || it.kind === 'deleted') return 'Open the Trash';
    if (it.project_state === 'gone') return 'No longer in your library';
    if (it.folder_id && (it.kind === 'moved' || it.kind === 'restored')) return 'Open the folder';
    if (it.kind === 'imported') return 'Open';
    return '';
  };

  return (
    <section className="mt-6" aria-labelledby="home-activity-heading" data-testid="home-activity">
      <div className="flex items-center justify-between mb-2">
        <h2 id="home-activity-heading" className="font-outfit text-base font-semibold text-foreground flex items-center gap-2"><History className="h-4 w-4 text-primary" aria-hidden="true" /> Recent activity</h2>
        {items && items.length > 0 && (
          <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground hover:text-foreground" onClick={clear} disabled={clearing} data-testid="home-activity-clear">{clearing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Eraser className="h-3.5 w-3.5 mr-1" aria-hidden="true" />} Clear</Button>
        )}
      </div>
      {error && <div className="text-sm text-destructive mb-2" data-testid="home-activity-error">{error} <Button variant="link" size="sm" className="h-auto p-0 ml-1" onClick={load}>Retry</Button></div>}
      {items === null && <div className="space-y-1.5" aria-busy="true">{[0, 1, 2].map((i) => <div key={i} className="h-9 rounded-md bg-muted animate-pulse" />)}</div>}
      {items && items.length === 0 && !error && (
        <div className="rounded-lg border border-dashed border-border px-4 py-5 text-center text-sm text-muted-foreground" data-testid="home-activity-empty">Nothing yet — saves, shares, copies, moves and imports will show up here so you can pick up where you left off.</div>
      )}
      {items && items.length > 0 && (
        <ol className="rounded-lg border border-border bg-card divide-y divide-border/60" data-testid="home-activity-list">
          {items.map((it) => {
            const Icon = ICONS[it.kind] || History;
            const actionable = !!hint(it) && it.project_state !== 'gone';
            const tone = it.kind === 'deleted' ? 'text-destructive/80' : it.kind === 'restored' || it.kind === 'imported' || it.kind === 'received' ? 'text-primary' : 'text-muted-foreground';
            return (
              <li key={it.id} data-testid={`home-activity-item-${it.id}`}>
                <button type="button" onClick={() => activate(it)} disabled={!actionable} title={hint(it) || undefined} className="group w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted/60 disabled:hover:bg-transparent disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md" data-testid={`home-activity-open-${it.id}`}>
                  <span className={`h-7 w-7 rounded-md bg-muted/70 flex items-center justify-center shrink-0 ${tone}`}><Icon className="h-3.5 w-3.5" aria-hidden="true" /></span>
                  <span className="flex-1 min-w-0 text-sm text-foreground truncate" data-testid={`home-activity-text-${it.id}`}>{describeActivity(it)}{it.project_state === 'trashed' && <span className="ml-1.5 text-[11px] text-muted-foreground">(in the Trash)</span>}</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums" data-testid={`home-activity-time-${it.id}`}>{relativeTime(it.at)}</span>
                  {actionable && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 shrink-0" aria-hidden="true" />}
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
};
